import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import React from 'react';
import { AnimatePresence, motion, useMotionValue, useSpring } from 'motion/react';
import { cn } from '@core/lib/utils';
import { ImageOff, Loader2, Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@core/components/ui/shared/dialogs/dialog';

type LinkPreviewProps = {
  children: React.ReactNode;
  url: string;
  className?: string;
  width?: number;
  height?: number;
  quality?: number;
  layout?: string;
  side?: "top" | "right" | "bottom" | "left";
  redirect?: boolean;
} & (
  | { isStatic: true; imageSrc: string }
  | { isStatic?: false; imageSrc?: never }
  );

export const LinkPreview = ({
  children,
  url,
  className,
  width = 200,
  height = 125,
  quality = 50,
  layout = "fixed",
  isStatic = false,
  imageSrc = "",
  side = "left",
  redirect = false,
}: LinkPreviewProps) => {
  const [isOpen, setOpen] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);
  const [isImageLoading, setIsImageLoading] = React.useState(true);
  const [cachedImageSrc, setCachedImageSrc] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    if (isOpen && !cachedImageSrc && !isStatic) {
      setIsImageLoading(true);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL();
            if (!redirect) {
              setCachedImageSrc(dataUrl);
            }
            setIsImageLoading(false);
          }
        } catch (error) {
          if (!redirect) {
            setCachedImageSrc(url);
          }
          setIsImageLoading(false);
        }
      };
      img.onerror = () => setIsImageLoading(false);
      img.src = url;
    } else if (isOpen && cachedImageSrc) {
      setIsImageLoading(false);
    }
  }, [isOpen, url, cachedImageSrc, isStatic, width, height, redirect]);

  const springConfig = { stiffness: 100, damping: 15 };
  const x = useMotionValue(0);
  const translateX = useSpring(x, springConfig);

  const handleMouseMove = (event: any) => {
    const targetRect = event.target.getBoundingClientRect();
    const eventOffsetX = event.clientX - targetRect.left;
    const offsetFromCenter = (eventOffsetX - targetRect.width / 2) / 2;
    x.set(offsetFromCenter);
  };

  const displaySrc = isStatic ? imageSrc : (cachedImageSrc || url);

  const previewContent = (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.6 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 260,
          damping: 20,
        },
      }}
      exit={{ opacity: 0, y: 20, scale: 0.6 }}
      className={cn("shadow-xl rounded-xl z-50", !redirect && "cursor-pointer")}
      style={{
        x: translateX,
      }}
      onClick={!redirect ? () => setDialogOpen(true) : undefined}
    >
      {redirect ? (
        <a
          href={url}
          className="block p-1 bg-white border-2 border-transparent shadow rounded-xl hover:border-neutral-200 dark:hover:border-neutral-800"
          style={{ fontSize: 0 }}
        >
          {isImageLoading && (
            <div
              className="flex items-center justify-center rounded-lg bg-muted"
              style={{ width: `${width}px`, height: `${height}px` }}
            >
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          <img
            src={displaySrc.trim()?.length > 0 ? displaySrc : undefined}
            width={width}
            height={height}
            className={cn("rounded-lg", (isImageLoading || url.trim()?.length <= 0) && "hidden")}
            alt="preview image"
            data-embed="screenshot.url"
            data-color-scheme="dark"
            data-viewport-is-mobile="true"
            data-viewport-device-scale-factor="1"
            data-viewport-width={width * 3}
            data-viewport-height={height * 3}
            style={{ width: `${width}px`, height: `${height}px`, objectFit: 'cover' }}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
          />
          {(!isImageLoading && url.trim()?.length <= 0) && (
            <div
              className="flex items-center justify-center rounded-lg bg-muted"
              style={{ width, height }}
            >
              <ImageOff />
            </div>
          )}
        </a>
      ) : (
        <div
          className="relative block p-1 bg-white border-2 border-transparent shadow rounded-xl hover:border-neutral-200 dark:hover:border-neutral-800"
          style={{ fontSize: 0 }}
        >
          {isImageLoading && (
            <div
              className="flex items-center justify-center rounded-lg bg-muted"
              style={{ width: `${width}px`, height: `${height}px` }}
            >
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          <img
            src={displaySrc.trim()?.length > 0 ? displaySrc : undefined}
            width={width}
            height={height}
            className={cn("rounded-lg", (isImageLoading || url.trim()?.length <= 0) && "hidden")}
            alt="preview image"
            data-embed="screenshot.url"
            data-color-scheme="dark"
            data-viewport-is-mobile="true"
            data-viewport-device-scale-factor="1"
            data-viewport-width={width * 3}
            data-viewport-height={height * 3}
            style={{ width: `${width}px`, height: `${height}px`, objectFit: 'cover' }}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
          />
          {(!isImageLoading && url.trim()?.length <= 0) && (
            <div
              className="flex items-center justify-center rounded-lg bg-muted"
              style={{ width, height }}
            >
              <ImageOff />
            </div>
          )}
          {!isImageLoading && displaySrc.trim()?.length > 0 && (
            <div className="absolute bottom-1 right-1 opacity-50 hover:opacity-100 transition-opacity duration-200 bg-black/50 rounded p-1">
              <Expand className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
      )}
    </motion.div>
  );

  return (
    <>
      <HoverCardPrimitive.Root
        openDelay={50}
        closeDelay={100}
        onOpenChange={(open) => {
          setOpen(open);
        }}
      >
        <HoverCardPrimitive.Trigger
          onMouseMove={handleMouseMove}
          className={cn("text-black dark:text-white", className)}
        >
          {children}
        </HoverCardPrimitive.Trigger>

        <HoverCardPrimitive.Content
          className="origin-(--radix-hover-card-content-transform-origin)"
          side={side}
          align="center"
          sideOffset={10}
        >
          <AnimatePresence>
            {isOpen && previewContent}
          </AnimatePresence>
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Root>

      {!redirect && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTitle className="hidden"/>
          <DialogContent className="max-w-md" aria-describedby={undefined}>
            <div className="flex justify-center">
              {isImageLoading ? (
                <div className="flex items-center justify-center bg-muted rounded-lg" style={{ width: '400px', height: '300px' }}>
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : url.trim()?.length > 0 ? (
                <img
                  src={url}
                  alt="Preview"
                  className="rounded-lg max-w-full max-h-full"
                  style={{ objectFit: 'contain' }}
                />
              ) : (
                <div className="flex items-center justify-center bg-muted rounded-lg" style={{ width: '400px', height: '300px' }}>
                  <ImageOff className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
